import { z } from 'zod';

export const HACKERONE_REPORT_STATES = [
  'new',
  'triaged',
  'needs-more-info',
  'resolved',
  'not-applicable',
  'informative',
  'duplicate',
  'spam',
  'retesting',
] as const;

export const HackeroneReportStateSchema = z.enum(HACKERONE_REPORT_STATES);
export type HackeroneReportState = z.infer<typeof HackeroneReportStateSchema>;

export const HackeroneSeveritySchema = z.enum([
  'none',
  'low',
  'medium',
  'high',
  'critical',
]);

export const HackeroneReportSchema = z.looseObject({
  id: z.string().min(1),
  title: z.string().min(1),
  state: HackeroneReportStateSchema,
  substate: z.string().nullish(),
  severity: z
    .object({
      rating: HackeroneSeveritySchema.nullish(),
    })
    .nullish(),
  weakness: z
    .object({
      name: z.string().nullish(),
      external_id: z.string().nullish(),
    })
    .nullish(),
  cve_ids: z.array(z.string()).optional(),
  reporter: z
    .object({
      username: z.string().nullish(),
    })
    .nullish(),
  vulnerability_information: z.string().nullish(),
  structured_scope: z
    .object({
      asset_identifier: z.string().nullish(),
      asset_type: z.string().nullish(),
    })
    .nullish(),
  url: z.url().nullish(),
  created_at: z.iso.datetime().nullish(),
  last_activity_at: z.iso.datetime().nullish(),
});

export type HackeroneReport = z.infer<typeof HackeroneReportSchema>;

const HackeroneJsonApiReportAttributesSchema = z.looseObject({
  title: z.string().nullish(),
  state: z.string().nullish(),
  substate: z.string().nullish(),
  severity: z
    .looseObject({
      rating: z.string().nullish(),
    })
    .nullish(),
  weakness: z
    .looseObject({
      name: z.string().nullish(),
      external_id: z.string().nullish(),
    })
    .nullish(),
  cve_ids: z.array(z.string()).optional(),
  vulnerability_information: z.string().nullish(),
  reporter: z
    .looseObject({
      username: z.string().nullish(),
    })
    .nullish(),
  structured_scope: z
    .looseObject({
      asset_identifier: z.string().nullish(),
      asset_type: z.string().nullish(),
    })
    .nullish(),
  url: z.string().nullish(),
  created_at: z.string().nullish(),
  last_activity_at: z.string().nullish(),
});

const HackeroneJsonApiReportItemSchema = z.looseObject({
  id: z
    .union([z.string(), z.number()])
    .transform(String)
    .pipe(z.string().min(1)),
  attributes: HackeroneJsonApiReportAttributesSchema.optional(),
});

type HackeroneJsonApiReportAttributes = z.infer<
  typeof HackeroneJsonApiReportAttributesSchema
>;

const parseHackeroneReportState = (
  value: string | null | undefined,
): HackeroneReportState => {
  const parsed = HackeroneReportStateSchema.safeParse(value);

  return parsed.success ? parsed.data : 'new';
};

const mapHackeroneReportSeverity = ({
  severity,
}: {
  severity: HackeroneJsonApiReportAttributes['severity'];
}): z.input<typeof HackeroneReportSchema>['severity'] => {
  if (!severity) {
    return severity;
  }

  const ratingResult = HackeroneSeveritySchema.safeParse(severity.rating);

  return {
    rating: ratingResult.success ? ratingResult.data : null,
  };
};

const toHackeroneReportInput = ({
  id,
  attributes,
}: {
  id: string;
  attributes: HackeroneJsonApiReportAttributes;
}): z.input<typeof HackeroneReportSchema> => ({
  id,
  title: attributes.title ?? `HackerOne report ${id}`,
  state: parseHackeroneReportState(attributes.state),
  substate: attributes.substate,
  severity: mapHackeroneReportSeverity({ severity: attributes.severity }),
  weakness: attributes.weakness,
  cve_ids: attributes.cve_ids,
  vulnerability_information: attributes.vulnerability_information,
  reporter: attributes.reporter,
  structured_scope: attributes.structured_scope,
  url: attributes.url,
  created_at: attributes.created_at,
  last_activity_at: attributes.last_activity_at,
});

export const parseHackeroneJsonApiReport = (
  item: unknown,
): HackeroneReport | null => {
  const itemResult = HackeroneJsonApiReportItemSchema.safeParse(item);

  if (!itemResult.success) {
    return null;
  }

  const reportResult = HackeroneReportSchema.safeParse(
    toHackeroneReportInput({
      id: itemResult.data.id,
      attributes: itemResult.data.attributes ?? {},
    }),
  );

  return reportResult.success ? reportResult.data : null;
};

export const HackeroneReportsListResponseSchema = z.looseObject({
  data: z.array(z.unknown()).optional(),
  links: z
    .looseObject({
      next: z.string().nullish(),
    })
    .optional(),
});

export type HackeroneReportsListResponse = z.infer<
  typeof HackeroneReportsListResponseSchema
>;
